import { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip, CircularProgress, Alert,
  ToggleButton, ToggleButtonGroup, Chip,
} from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { toast } from 'react-toastify';
import warrantyTicketServices from '../services/warrantyTicketServices';
import propertiesServices from '../services/propertiesServices';

/**
 * Equipment Photos section attached to a warranty ticket.
 *
 * Shows photo thumbnails attached to the ticket, lets the technician open
 * the property's CompanyCam project to take new photos, and provides a
 * picker to attach existing PropertyMedia photos to the ticket.
 *
 * Lightbox is rendered inline (a simple MUI Dialog with the full-size
 * image) so we don't pull in another dependency.
 */
export default function WarrantyTicketPhotoSection({ ticket, canEdit = true }) {
  const ticketId = ticket?.id;
  const propertyId = ticket?.property?.id;
  const ticketCreatedAt = ticket?.createdAt;

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [ccProjects, setCcProjects] = useState([]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerFilter, setPickerFilter] = useState('after'); // 'after' | 'all'
  const [pickerSelected, setPickerSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const [lightboxUrl, setLightboxUrl] = useState(null);

  const reload = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const res = await warrantyTicketServices.listPhotos(ticketId);
      setPhotos(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load attached photos', e);
    }
    setLoading(false);
  }, [ticketId]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!propertyId) return;
    propertiesServices
      .listCcProjects(propertyId)
      .then((res) => setCcProjects(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCcProjects([]));
  }, [propertyId]);

  const openPicker = async () => {
    if (!ticketId) return;
    setPickerOpen(true);
    setPickerLoading(true);
    setPickerSelected(new Set());
    try {
      const res = await warrantyTicketServices.listAvailablePhotos(ticketId);
      setPickerItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load available photos', e);
      toast.error('Failed to load photos from this property');
    }
    setPickerLoading(false);
  };

  const togglePick = (mediaId, alreadyAttached) => {
    if (alreadyAttached) return;
    setPickerSelected((prev) => {
      const next = new Set(prev);
      if (next.has(mediaId)) next.delete(mediaId);
      else next.add(mediaId);
      return next;
    });
  };

  const confirmAttach = async () => {
    const ids = Array.from(pickerSelected);
    if (ids.length === 0) {
      setPickerOpen(false);
      return;
    }
    setSaving(true);
    try {
      const res = await warrantyTicketServices.attachPhotos(ticketId, ids);
      const attached = res?.data?.attached ?? ids.length;
      toast.success(`Attached ${attached} photo(s)`);
      setPickerOpen(false);
      await reload();
    } catch (e) {
      console.error('Attach failed', e);
      toast.error('Failed to attach photos');
    }
    setSaving(false);
  };

  const detach = async (mediaId) => {
    try {
      await warrantyTicketServices.detachPhoto(ticketId, mediaId);
      toast.success('Photo detached');
      await reload();
    } catch (e) {
      console.error('Detach failed', e);
      toast.error('Failed to detach photo');
    }
  };

  const ccUrl = ccProjects[0]?.ccProjectUrl;

  // Filter rule: show photos taken AFTER the ticket was created — these are
  // the most likely candidates (the tech just took them for this ticket).
  // Keep already-attached photos visible in both modes so the tech can see
  // what is already linked.
  const filteredPickerItems = (() => {
    if (pickerFilter === 'all') return pickerItems;
    if (!ticketCreatedAt) return pickerItems;
    const t = new Date(ticketCreatedAt).getTime();
    return pickerItems.filter((m) => {
      if (m.attached) return true;
      const ca = m.resource?.createdAt ? new Date(m.resource.createdAt).getTime() : 0;
      return ca >= t;
    });
  })();

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2 }} variant="outlined">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
          Equipment Photos ({photos.length})
        </Typography>
        {ccUrl && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            component="a"
            href={ccUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open CompanyCam
          </Button>
        )}
        {canEdit && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddAPhotoIcon />}
            onClick={openPicker}
            disabled={!propertyId}
          >
            Attach photos
          </Button>
        )}
      </Box>

      {photos.length === 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          No photos attached yet. Take a photo of the equipment label
          (model + serial number) in CompanyCam, then come back and tap
          "Attach photos" — your new photo will appear in the list.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
            gap: 1,
          }}
        >
          {photos.map((p) => {
            const url = p.propertyMedia?.resource?.url;
            if (!url) return null;
            return (
              <Box
                key={p.linkId}
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '1 / 1',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover .photo-actions': { opacity: 1 },
                }}
                onClick={() => setLightboxUrl(url)}
              >
                <img
                  src={url}
                  alt={p.propertyMedia?.name || 'equipment photo'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                {canEdit && (
                  <Box
                    className="photo-actions"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      opacity: 0.7,
                      transition: 'opacity 0.15s',
                    }}
                  >
                    <Tooltip title="Detach from ticket">
                      <IconButton
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'white' } }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Detach this photo from the ticket? (The photo itself stays in the property gallery.)')) {
                            detach(p.propertyMedia.id);
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* ---------- Lightbox ---------- */}
      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#000' } }}
      >
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton
            onClick={() => setLightboxUrl(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          {lightboxUrl && (
            <img
              src={lightboxUrl}
              alt="full size"
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                display: 'block',
                objectFit: 'contain',
              }}
            />
          )}
        </Box>
      </Dialog>

      {/* ---------- Picker dialog ---------- */}
      <Dialog
        open={pickerOpen}
        onClose={() => (saving ? null : setPickerOpen(false))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Attach photos to ticket
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={pickerFilter}
            exclusive
            onChange={(_, v) => v && setPickerFilter(v)}
          >
            <ToggleButton value="after">Since ticket created</ToggleButton>
            <ToggleButton value="all">All property photos</ToggleButton>
          </ToggleButtonGroup>
        </DialogTitle>
        <DialogContent dividers>
          {pickerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredPickerItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {pickerFilter === 'after'
                ? 'No new photos since the ticket was created. Switch to "All property photos" or take a new photo in CompanyCam first.'
                : 'This property has no photos yet.'}
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Tap photos to select. Already-attached photos are shown with a green check.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                  gap: 1,
                }}
              >
                {filteredPickerItems.map((m) => {
                  const url = m.resource?.url;
                  if (!url) return null;
                  const selected = pickerSelected.has(m.id);
                  const isNew = ticketCreatedAt && m.resource?.createdAt
                    && new Date(m.resource.createdAt).getTime() >= new Date(ticketCreatedAt).getTime();
                  return (
                    <Box
                      key={m.id}
                      onClick={() => togglePick(m.id, m.attached)}
                      sx={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '1 / 1',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '2px solid',
                        borderColor: m.attached
                          ? 'success.main'
                          : selected
                            ? 'primary.main'
                            : 'divider',
                        cursor: m.attached ? 'default' : 'pointer',
                        opacity: m.attached ? 0.55 : 1,
                      }}
                    >
                      <img
                        src={url}
                        alt={m.name || 'photo'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          bgcolor: 'rgba(255,255,255,0.85)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 26,
                          height: 26,
                        }}
                      >
                        {m.attached ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : selected ? (
                          <CheckCircleIcon fontSize="small" color="primary" />
                        ) : (
                          <RadioButtonUncheckedIcon fontSize="small" />
                        )}
                      </Box>
                      {isNew && !m.attached && (
                        <Chip
                          size="small"
                          label="NEW"
                          color="warning"
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            left: 4,
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmAttach}
            disabled={saving || pickerSelected.size === 0}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Attach {pickerSelected.size > 0 ? `(${pickerSelected.size})` : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
